/**
 * Versioned schema changes for the CLI local SQLite DB, modeled after {@link migrateDurableObject}
 * in `packages/durable-objects`: ordered migrations, `__migrations` bookkeeping, optional checkpoints.
 *
 * SQL lives under `./migrations/*.sql` (see {@link readCliSqliteMigrationSql}).
 * Legacy on-disk JSON → SQLite import is **not** done here; see `importCliSqliteLegacyDiskData` in
 * `legacy_migration/import-cli-legacy-disk-data.ts` after the DB is opened and migrations have run.
 */

import type { Database } from 'node-sqlite3-wasm';
import { readCliSqliteMigrationSql } from './read-migration-sql.js';
import { runSqliteMigrations, type SqliteMigration } from './run-sqlite-migrations.js';

export type CliSqliteMigration = SqliteMigration;

const CHECKPOINT_V1 = '001_cli_sqlite_checkpoint_v1';

const CHECKPOINT_V1_SQL = readCliSqliteMigrationSql('001_cli_sqlite_checkpoint_v1.sql');
const AGENT_CAPABILITIES_SQL = readCliSqliteMigrationSql('002_agent_capabilities.sql');
const DROP_CACHE_TABLES_SQL = readCliSqliteMigrationSql('006_drop_cache_tables.sql');

function agentCapabilitiesTableState(db: Database): 'new' | 'legacy' | 'current' {
  const rows = db.all(
    `SELECT name FROM sqlite_master WHERE type='table' AND name IN ('agent_capabilities', 'agent_capability_cache')`,
  ) as Array<{ name: string }>;
  const names = new Set(rows.map((r) => r.name));
  if (names.has('agent_capabilities')) return 'current';
  if (names.has('agent_capability_cache')) return 'legacy';
  return 'new';
}

export const CLI_SQLITE_MIGRATIONS: CliSqliteMigration[] = [
  {
    name: CHECKPOINT_V1,
    checkpoint: true,
    migrate: (db) => {
      db.exec(CHECKPOINT_V1_SQL);
    },
  },
  {
    name: '002_agent_capabilities',
    migrate: (db) => {
      const state = agentCapabilitiesTableState(db);
      if (state === 'current') return;
      if (state === 'legacy') {
        db.exec(`
          ALTER TABLE agent_capability_cache RENAME TO agent_capabilities;
          DROP INDEX IF EXISTS idx_agent_capability_cache_workspace;
          CREATE INDEX IF NOT EXISTS idx_agent_capabilities_workspace ON agent_capabilities(workspace_id);
        `);
        return;
      }
      db.exec(AGENT_CAPABILITIES_SQL);
    },
    alreadyApplied: (db) => agentCapabilitiesTableState(db) === 'current',
  },
  {
    name: '006_drop_cache_tables',
    migrate: (db) => {
      db.exec(DROP_CACHE_TABLES_SQL);
    },
    alreadyApplied: (db) => {
      const row = db.get(
        `SELECT 1 as ok FROM sqlite_master
         WHERE type='table' AND name IN ('file_index_parent_path', 'file_index_child_path', 'file_index_path')
         LIMIT 1`,
      ) as { ok: number } | null;
      return row == null;
    },
  },
];

export function migrateCliSqlite(db: Database): void {
  runSqliteMigrations(
    db,
    CLI_SQLITE_MIGRATIONS,
    readCliSqliteMigrationSql('000_bootstrap_migrations_table.sql'),
    'cli-sqlite',
  );
}
